import { aankoopMult } from '@/lib/artikelPrijs';
import { scanDiscountPercent, scanDiscountMode } from '@/lib/scanDiscount';

const number = value => Number(String(value ?? 0).replace(',', '.')) || 0;
export const scanPackSize = item => number(item.eenheid_aantal) > 0 ? number(item.eenheid_aantal) : 1;
export const scanCorrection = item => item.stuks_correctie != null ? number(item.stuks_correctie)
  : number(item.aantal) * (number(item.stuks_effectief ?? scanPackSize(item)) - scanPackSize(item));
export const scanTotal = item => number(item.aantal) * scanPackSize(item) + scanCorrection(item);
export const scanPiecePrice = item => item.prijs_per_stuk ? number(item.prijs) : number(item.prijs) / scanPackSize(item);
export const scanValid = item => (item.cash_edit ? Number.isFinite(Number(item.aantal)) : Number.isInteger(number(item.aantal))) && number(item.aantal) > 0
  && (item.cash_edit ? Number.isFinite(scanTotal(item)) && Math.abs(scanTotal(item)) <= Number.MAX_SAFE_INTEGER : Number.isSafeInteger(scanTotal(item))) && scanTotal(item) !== 0;

export const scanAmountCorrection = line => line.scan_bedragcorrectie === true || (!!line.scan_prijs_precisie && number(line.aantal) < 0);
export const scanHasContent = item => !!(item.art_id || item.omschrijving?.trim() || number(item.prijs));
export function knownPurchasePrice(article) {
  const raw = article?.aankoopprijs;
  if (raw == null || typeof raw === 'boolean' || (typeof raw === 'string' && !raw.trim())) return null;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

export function scanCostMultiplier(article, line) {
  if (scanAmountCorrection(line)) return 0;
  const scanned = line.scan_prijs_precisie || line.scan_stuks_per_pack;
  const divisor = line.eenheid === 'st' && number(line.scan_stuks_per_pack) > 0 ? number(line.scan_stuks_per_pack) : 1;
  // Match purchase and sale units. A pallet's L/M or m² coverage is not a pack cost multiplier.
  // Partial scan lines still consume only their fraction of the original sold unit.
  if (scanned && String(article?.eenheid || '').toLowerCase() !== 'pack') return 1 / divisor;
  const size = number(line.scan_stuks_per_pack || line.eenheid_aantal);
  const effective = scanned && size > 0 ? { ...article, pack_grootte: size } : article;
  return aankoopMult(effective) / divisor;
}

export function scanArticle(art) {
  const variants = art.varianten?.length ? art.varianten : (art.variant_matrix?.rijen || []);
  if (!variants.length) return art;
  const names = [...new Set(variants.flatMap(v => Object.keys(v.combo || {})))];
  const dims = art.dimensies?.length ? art.dimensies : names.map(naam => ({ naam }));
  const dimensies = dims.map(d => ({ ...d, waardes: d.waardes?.length ? d.waardes : d.waarden?.length ? d.waarden
    : [...new Set(variants.map(v => v.combo?.[d.naam]).filter(v => v != null && v !== ''))] }));
  return { ...art, varianten_actief: true, varianten: variants, dimensies };
}

export function scanDocumentLine(item, btwOverride) {
  const size = scanPackSize(item);
  const total = scanTotal(item);
  const originalSize = number(item.artikel_pack_grootte) || size;
  const corrected = scanCorrection(item) !== 0 || size !== originalSize;
  const missing = number(item.aantal) * size - total;
  return {
    art_id: item.art_id, referentie: item.art_code || '',
    omschr: item.omschrijving + (item.variant_label ? ` · ${item.variant_label}` : '') +
      (total < 0 ? ` (${total} ${total === -1 ? 'stuk' : 'stuks'}; bedragcorrectie)`
        : corrected ? ` (${total} stuks; ${item.aantal} × ${size}${missing > 0 ? `, ${missing} ${missing === 1 ? 'stuk' : 'stuks'} tekort` : missing < 0 ? `, ${-missing} ${missing === -1 ? 'stuk' : 'stuks'} extra` : ''})` : ''),
    aantal: corrected ? total : number(item.aantal),
    prijs: corrected ? scanPiecePrice(item) : scanPiecePrice(item) * size,
    scan_prijs_precisie: true,
    scan_prijs_handmatig: !!item.prijs_handmatig,
    scan_bedragcorrectie: total < 0,
    korting: scanDiscountPercent(item, total * scanPiecePrice(item)),
    ...(scanDiscountMode(item) === 'euro' ? { scan_korting_mode: 'euro', scan_korting_euro: number(item.korting) } : {}),
    btw: item.btw_handmatig ? item.btw : (btwOverride ?? (item.btw ?? 21)),
    btw_handmatig: !!item.btw_handmatig,
    eenheid: corrected ? 'st' : (item.eenheid || 'st'),
    eenheid_aantal: corrected ? 0 : (item.eenheid_aantal || 0),
    eenheid_label: corrected ? '' : (item.eenheid_label || ''), stuks_effectief: 0,
    toon_eenheid_prijs: !corrected && item.eenheid_aantal > 0 && !!item.eenheid_label,
    // The line is in pieces; inventory and purchase prices remain in the original pack unit.
    ...(corrected ? { scan_stuks_per_pack: originalSize, scan_aantal_packs: number(item.aantal) } : {}),
    ean: item.ean || '', toon_ean: false,
    combo_key: item.variant_combo_key || item.variant_code || '',
    variant_combo: item.variant_combo || null, variant_label: item.variant_label || '',
    pack_info: '', ref_search: '', gewicht: '',
  };
}